import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/auth/session";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { useGate } from "@/components/site/gateContext";
import LockedPlace from "@/components/site/LockedPlace";
import NotFound from "@/components/site/NotFound";
import type { PreviewKind } from "@/lib/previews";
import PreviewFor from "@/components/site/previews/Previews";
import { PAGE_LOADERS, PreloadedPostingDetail, PreloadedResumeCheck } from "@/lib/pageLoaders";
// The home page, in the first bundle so the first visit draws it at once.
import Jobs from "@/pages/Jobs";
import { postingDetailPath, ROUTES } from "@/lib/routes";
import { joinScreen, withoutJoin } from "@/lib/site";

const Account = lazy(PAGE_LOADERS[ROUTES.ACCOUNT]);
const Overview = lazy(PAGE_LOADERS[ROUTES.OVERVIEW]);
const OverviewPreview = lazy(() => import("@/components/site/previews/OverviewPreview"));
const Queue = lazy(PAGE_LOADERS[ROUTES.QUEUE]);
const History = lazy(PAGE_LOADERS[ROUTES.HISTORY]);
const Saved = lazy(PAGE_LOADERS[ROUTES.SAVED]);
const Envoy = lazy(PAGE_LOADERS[ROUTES.ENVOY]);
const LazyPostingDetail = lazy(() => import("@/pages/PostingDetail"));
const Company = lazy(() => import("@/pages/Company"));
const Resume = lazy(PAGE_LOADERS[ROUTES.RESUME]);
const ResumeEditor = lazy(() => import("@/pages/ResumeEditor"));
const ResumeWork = lazy(() => import("@/pages/ResumeWork"));
const Openings = lazy(PAGE_LOADERS[ROUTES.OPENINGS]);
const Pricing = lazy(PAGE_LOADERS[ROUTES.PRICING]);
const Support = lazy(() => import("@/pages/Support"));
const Subscribed = lazy(() => import("@/pages/Subscribed"));
const Tools = lazy(() => import("@/pages/Tools"));
const LazyResumeCheck = lazy(() => import("@/pages/ResumeCheck"));

/** A posting's page: at once when its code came first, as a server's page needs. */
function PostingDetail() {
    return PreloadedPostingDetail ? <PreloadedPostingDetail /> : <LazyPostingDetail />;
}

/** The checker: at once when its code came first, as a server's page of it needs. */
function ResumeCheck() {
    return PreloadedResumeCheck ? <PreloadedResumeCheck /> : <LazyResumeCheck />;
}

/**
 * A place an account keeps: the place itself for someone signed in, and
 * for a visitor the place shown with the example in it and the one way
 * in (`preview`), or, for Account, the page that says what it holds.
 * Nothing while the sign-in is still being read.
 */
function MemberOnly({
    preview,
    children,
}: {
    preview: PreviewKind | "overview" | "account";
    children: ReactNode;
}) {
    const { state } = useAuth();
    if (state.status === "loading") return null;
    if (state.status === "signed-out")
        return preview === "account" ? (
            <LockedPlace />
        ) : preview === "overview" ? (
            <OverviewPreview />
        ) : (
            <PreviewFor kind={preview} />
        );
    return <>{children}</>;
}

/**
 * `?join=sign-up` or `?join=sign-in` on any page, from the extension, an
 * email or an old link: the sheet it asks for, over the page, signed out.
 * Signed in, the ask is spent and dropped from the address.
 */
function JoinFromAddress() {
    const { state } = useAuth();
    const { ask } = useGate();
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const screen = joinScreen(search);
    useEffect(() => {
        if (!screen || state.status === "loading") return;
        if (state.status === "signed-out") ask(screen === "sign-up" ? "join" : "signin");
        navigate(`${pathname}${withoutJoin(search)}`, { replace: true });
    }, [screen, state.status, ask, navigate, pathname, search]);
    return null;
}

/** For you is the envoy's own board: a visitor has no envoy, so they see every job. */
function ForYou() {
    const { state } = useAuth();
    const { search } = useLocation();
    if (state.status === "signed-out") return <Navigate to={`${ROUTES.HOME}${search}`} replace />;
    return (
        <MemberOnly preview="envoy">
            <Openings />
        </MemberOnly>
    );
}

/** Every place on the site, for everyone; the frame is the caller's (Root). */
export default function App() {
    const { pathname } = useLocation();
    return (
        <ErrorBoundary resetKey={pathname}>
            <JoinFromAddress />
            <Suspense fallback={null}>
                <Routes>
                    <Route path={ROUTES.HOME} element={<Jobs />} />
                    <Route path={ROUTES.JOBS} element={<Jobs />} />
                    {/* Under its list. The list route is a complete path,
                    so there is nothing for the id to shadow. */}
                    <Route path={ROUTES.POSTING_DETAIL} element={<PostingDetail />} />
                    <Route path={ROUTES.COMPANY} element={<Company />} />
                    <Route path={ROUTES.OPENINGS} element={<ForYou />} />
                    <Route path={ROUTES.PRICING} element={<Pricing />} />
                    <Route path={ROUTES.SUPPORT} element={<Support />} />
                    <Route path={ROUTES.SUBSCRIBED} element={<Subscribed />} />
                    <Route path={ROUTES.TOOLS} element={<Tools />} />
                    <Route path={ROUTES.RESUME_CHECK} element={<ResumeCheck />} />
                    <Route path={ROUTES.RESUME_CHECK_POSTING} element={<ResumeCheck />} />
                    <Route
                        path={ROUTES.SAVED}
                        element={
                            <MemberOnly preview="saved">
                                <Saved />
                            </MemberOnly>
                        }
                    />
                    {[ROUTES.APPLICATIONS, ROUTES.QUEUE].map((path) => (
                        <Route
                            key={path}
                            path={path}
                            element={
                                <MemberOnly preview="applications">
                                    <Queue />
                                </MemberOnly>
                            }
                        />
                    ))}
                    <Route
                        path={ROUTES.HISTORY}
                        element={
                            <MemberOnly preview="applications">
                                <History />
                            </MemberOnly>
                        }
                    />
                    <Route
                        path={ROUTES.RESUME}
                        element={
                            <MemberOnly preview="resume">
                                <Resume />
                            </MemberOnly>
                        }
                    />
                    <Route
                        path={ROUTES.RESUME_EDIT}
                        element={
                            <MemberOnly preview="resume">
                                <ResumeEditor />
                            </MemberOnly>
                        }
                    />
                    <Route
                        path={ROUTES.RESUME_WORK}
                        element={
                            <MemberOnly preview="resume">
                                <ResumeWork />
                            </MemberOnly>
                        }
                    />
                    <Route
                        path={ROUTES.ENVOY}
                        element={
                            <MemberOnly preview="envoy">
                                <Envoy />
                            </MemberOnly>
                        }
                    />
                    <Route
                        path={ROUTES.OVERVIEW}
                        element={
                            <MemberOnly preview="overview">
                                <Overview />
                            </MemberOnly>
                        }
                    />
                    <Route
                        path={ROUTES.ACCOUNT}
                        element={
                            <MemberOnly preview="account">
                                <Account />
                            </MemberOnly>
                        }
                    />
                    {/* The first versions' paths, kept so a bookmark still lands. */}
                    <Route path="/activity" element={<Navigate to={ROUTES.OVERVIEW} replace />} />
                    <Route path="/boards" element={<Navigate to={ROUTES.HOME} replace />} />
                    <Route path="/postings" element={<Navigate to={ROUTES.HOME} replace />} />
                    <Route path="/postings/:postingId" element={<RedirectPosting />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </ErrorBoundary>
    );
}

function RedirectPosting() {
    const { postingId = "" } = useParams();
    return <Navigate to={postingDetailPath(postingId)} replace />;
}
